import React, { useEffect, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { t as tRaw } from '@/locales/strings';

const DISMISS_KEY = 'bb_install_prompt_dismissed';

// Captures the Android/Chrome beforeinstallprompt event and surfaces
// a small "install" card after the user has spent enough time in the
// app to feel it's worth keeping. iOS Safari does not fire
// beforeinstallprompt so this is silent there — the iOS share-sheet
// "Add to Home Screen" flow is what those users see and there is no
// JS hook for it.
//
// Dismiss is sticky per-device (localStorage) so we don't nag on
// every visit. The card also hides automatically once the PWA is
// installed (standalone display mode).

function isStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  return !!window.navigator.standalone;
}

export default function InstallPrompt() {
  const { lang, user, userProgress } = useApp();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return !!localStorage.getItem(DISMISS_KEY); } catch { return false; }
  });

  useEffect(() => {
    if (hidden) return;
    if (isStandalone()) return;

    function onPrompt(e) {
      e.preventDefault();
      setDeferred(e);
    }
    function onInstalled() {
      setDeferred(null);
      setVisible(false);
      try { localStorage.setItem(DISMISS_KEY, 'installed'); } catch {}
    }
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [hidden]);

  // Only offer the prompt after the user has:
  //   1) authenticated (no point nagging anonymous visitors), and
  //   2) completed at least one session.
  // userProgress defaults to DEFAULT_PROGRESS (all zeros) when no row
  // is loaded, so the session count check naturally gates the prompt
  // until real data arrives. Re-evaluating on userProgress change
  // fires the captured deferred prompt the moment sessions >= 1
  // lands, even if beforeinstallprompt was received earlier.
  useEffect(() => {
    if (!deferred) { setVisible(false); return; }
    if (!user) return;
    const sessions = userProgress?.total_sessions_completed;
    if (Number.isFinite(sessions) && sessions >= 1) setVisible(true);
  }, [deferred, user, userProgress]);

  if (!visible || hidden || !deferred) return null;

  async function accept() {
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch { /* user cancelled — treat same as dismiss */ }
    setDeferred(null);
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, 'prompted'); } catch {}
  }

  function dismiss() {
    setHidden(true);
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, 'dismissed'); } catch {}
  }

  return (
    <div
      role="dialog"
      aria-labelledby="install-prompt-title"
      style={{
        position: 'fixed',
        left: 12, right: 12,
        bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
        zIndex: 38000,
        background: 'var(--surface, #FFFFFF)',
        border: '1px solid var(--border, #E8DDD4)',
        borderRadius: 14,
        padding: '14px 16px',
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
        fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
        maxWidth: 460,
        margin: '0 auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 40, height: 40, flexShrink: 0,
          borderRadius: 10,
          background: 'var(--primary-pale, #F5D6E0)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--primary, #A8003F)',
          fontFamily: "var(--font-title, Georgia, serif)",
          fontStyle: 'italic', fontSize: 20, fontWeight: 500,
        }}>
          Bb
        </div>
        <div style={{ flex: 1 }}>
          <div
            id="install-prompt-title"
            style={{
              fontSize: 14, fontWeight: 700,
              color: 'var(--dark-brown, #5A3E2B)',
              marginBottom: 2,
            }}
          >
            {tRaw('install_prompt_title', lang)}
          </div>
          <div style={{
            fontSize: 13,
            color: 'var(--warm-brown, #8B7355)',
            lineHeight: 1.4,
          }}>
            {tRaw('install_prompt_body', lang)}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={dismiss}
          style={{
            padding: '8px 14px',
            background: 'transparent',
            border: 'none',
            color: 'var(--warm-brown, #8B7355)',
            fontSize: 12, fontFamily: 'inherit',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {tRaw('install_prompt_later', lang)}
        </button>
        <button
          type="button"
          onClick={accept}
          style={{
            padding: '8px 18px',
            background: 'var(--primary, #A8003F)',
            color: '#fff',
            border: 'none',
            borderRadius: 999,
            fontSize: 12, fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontFamily: 'inherit',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {tRaw('install_prompt_cta', lang)}
        </button>
      </div>
    </div>
  );
}
